'use client';
import React, { useState, useCallback } from 'react';
import { Search, MapPin, Tag, Eye, Heart, ArrowRight, ChevronRight, Grid, List } from 'lucide-react';
import Link from 'next/link';
import AppLayout from '@/components/AppLayout';
import HomeButton from '@/components/HomeButton';

interface ClassifiedListing {
  id: string;
  title: string;
  price: number;
  location: string;
  category: string;
  condition: string;
  posted: string;
  views: number;
  image_url: string;
  seller: string;
  is_featured: boolean;
  description: string;
}

const LISTINGS: ClassifiedListing[] = [
{ id: '1', title: 'iPhone 15 Pro Max 256GB — Space Black', price: 145000, location: 'Nairobi CBD', category: 'Electronics', condition: 'Used', posted: '2 hours ago', views: 234, image_url: "https://img.rocket.new/generatedImages/rocket_gen_img_153be61b7-1774419612914.png", seller: 'Peter M', is_featured: true, description: 'Excellent condition, no scratches. Comes with original box and charger.' },
{ id: '2', title: 'Toyota Vitz 2018 — 1300cc Automatic', price: 850000, location: 'Westlands', category: 'Vehicles', condition: 'Used', posted: '5 hours ago', views: 567, image_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1bf2e0883-1783837235616.png", seller: 'Samuel K', is_featured: true, description: 'Low mileage, accident-free. Full service history available.' },
{ id: '3', title: 'Samsung 55" 4K Smart TV', price: 65000, location: 'Kilimani', category: 'Electronics', condition: 'New', posted: '1 day ago', views: 123, image_url: "https://images.unsplash.com/photo-1603477243907-944895e59197", seller: 'Faith N', is_featured: false, description: 'Brand new in box. Selling due to relocation.' },
{ id: '4', title: 'Sofa Set 7-Seater — Brown Leather', price: 45000, location: 'Kasarani', category: 'Furniture', condition: 'Used', posted: '2 days ago', views: 89, image_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1dac5809d-1774554943721.png", seller: 'Mary W', is_featured: false, description: 'Good condition. Minor wear on armrests. Must collect.' },
{ id: '5', title: 'MacBook Pro M2 14" — 16GB RAM', price: 185000, location: 'Nairobi CBD', category: 'Electronics', condition: 'Used', posted: '3 hours ago', views: 445, image_url: "https://img.rocket.new/generatedImages/rocket_gen_img_19a8e4e77-1772398843779.png", seller: 'David O', is_featured: true, description: 'Barely used. 512GB SSD. Comes with charger and sleeve.' },
{ id: '6', title: 'Gas Cooker 4-Burner Stainless Steel', price: 18500, location: 'Mombasa', category: 'Home Appliances', condition: 'New', posted: '4 days ago', views: 67, image_url: "https://img.rocket.new/generatedImages/rocket_gen_img_14cfff6ec-1768751777607.png", seller: 'Esther A', is_featured: false, description: 'Brand new, still in packaging. Selling at cost price.' },
{ id: '7', title: 'Mountain Bike — Trek Marlin 7', price: 55000, location: 'Karen', category: 'Sports', condition: 'Used', posted: '1 day ago', views: 178, image_url: "https://img.rocket.new/generatedImages/rocket_gen_img_100ecdcd8-1772654120761.png", seller: 'John K', is_featured: false, description: 'Lightly used. New tyres and brakes fitted last month.' },
{ id: '8', title: 'Office Desk + Chair Set', price: 22000, location: 'Upperhill', category: 'Furniture', condition: 'Used', posted: '6 days ago', views: 45, image_url: "https://images.unsplash.com/photo-1590177600178-c2597bd63ea7", seller: 'Grace O', is_featured: false, description: 'Ergonomic chair and L-shaped desk. Good condition.' }];


const CATEGORIES = ['All', 'Electronics', 'Vehicles', 'Furniture', 'Home Appliances', 'Sports', 'Fashion'];
const CONDITIONS = ['Any Condition', 'New', 'Used'];
const LOCATIONS = ['All Locations', 'Nairobi CBD', 'Westlands', 'Kilimani', 'Kasarani', 'Mombasa', 'Karen'];

export default function ClassifiedsLandingPage() {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [condition, setCondition] = useState('Any Condition');
  const [location, setLocation] = useState('All Locations');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');

  const filtered = LISTINGS.filter((l) => {
    const matchSearch = !search || l.title.toLowerCase().includes(search.toLowerCase());
    const matchCat = category === 'All' || l.category === category;
    const matchCond = condition === 'Any Condition' || l.condition === condition;
    const matchLoc = location === 'All Locations' || l.location.includes(location.replace(' CBD', ''));
    const matchMin = !minPrice || l.price >= Number(minPrice);
    const matchMax = !maxPrice || l.price <= Number(maxPrice);
    return matchSearch && matchCat && matchCond && matchLoc && matchMin && matchMax;
  });

  const toggleSave = useCallback((id: string) => {
    setSavedIds((prev) => {const n = new Set(prev);n.has(id) ? n.delete(id) : n.add(id);return n;});
  }, []);

  const formatPrice = (p: number) => `KES ${p.toLocaleString()}`;

  return (
    <AppLayout>
      <div className="min-h-screen" style={{ background: '#070C1A' }}>
        {/* Hero */}
        <div className="relative px-4 pt-8 pb-10 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #1a1200 0%, #0d1a00 50%, #070C1A 100%)' }}>
          <div className="absolute inset-0 opacity-20"
          style={{ backgroundImage: 'radial-gradient(circle at 25% 50%, #FFB300 0%, transparent 50%), radial-gradient(circle at 75% 30%, #FF6D00 0%, transparent 50%)' }} />
          <div className="relative max-w-2xl mx-auto text-center">
            <div className="flex items-center justify-between mb-4">
              <HomeButton />
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-600"
              style={{ background: 'rgba(255,179,0,0.15)', color: '#FFB300', border: '1px solid rgba(255,179,0,0.3)' }}>
                <Tag size={12} /> {28450 .toLocaleString()} Listings Available
              </div>
              <Link href="/create-listing?type=classified" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-700"
                style={{ background: '#FFB300', color: '#000' }}>
                + Post Ad
              </Link>
            </div>
            <h1 className="text-3xl font-800 text-white mb-3 leading-tight">
              Buy & Sell <span style={{ color: '#FFB300' }}>Anything</span> in Kenya
            </h1>
            <p className="text-sm mb-6" style={{ color: 'rgba(255,255,255,0.55)' }}>
              Electronics, vehicles, furniture, and more. Find great deals near you.
            </p>
            <div className="relative max-w-md mx-auto">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: 'rgba(255,255,255,0.4)' }} />
              <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search listings…"
              className="w-full pl-10 pr-4 py-3.5 rounded-2xl text-sm outline-none"
              style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff' }} />
            </div>
          </div>
        </div>

        {/* Filters bar */}
        <div className="px-4 py-3 sticky top-0 z-20"
        style={{ background: 'rgba(7,12,26,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="max-w-4xl mx-auto flex items-center gap-2 overflow-x-auto pb-1">
            {CATEGORIES.map((c) =>
            <button key={c} onClick={() => setCategory(c)}
            className="px-3 py-2 rounded-xl text-xs font-600 flex-shrink-0 transition-all"
            style={{ background: category === c ? 'rgba(255,179,0,0.15)' : 'rgba(255,255,255,0.04)', color: category === c ? '#FFB300' : 'rgba(255,255,255,0.55)', border: `1px solid ${category === c ? 'rgba(255,179,0,0.3)' : 'rgba(255,255,255,0.08)'}` }}>
                {c}
              </button>
            )}
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 pt-4 pb-8">
          {/* Sub-filters */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <select value={location} onChange={(e) => setLocation(e.target.value)}
            className="px-3 py-2 rounded-xl text-xs outline-none"
            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)' }}>
              {LOCATIONS.map((l) => <option key={l} value={l} style={{ background: '#0D1526' }}>{l}</option>)}
            </select>
            <select value={condition} onChange={(e) => setCondition(e.target.value)}
            className="px-3 py-2 rounded-xl text-xs outline-none"
            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)' }}>
              {CONDITIONS.map((c) => <option key={c} value={c} style={{ background: '#0D1526' }}>{c}</option>)}
            </select>
            <input value={minPrice} onChange={(e) => setMinPrice(e.target.value)} placeholder="Min KES"
            className="w-24 px-3 py-2 rounded-xl text-xs outline-none"
            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)' }} />
            <input value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} placeholder="Max KES"
            className="w-24 px-3 py-2 rounded-xl text-xs outline-none"
            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)' }} />
            <div className="ml-auto flex items-center gap-1">
              <button onClick={() => setViewMode('grid')} className="p-2 rounded-lg"
              style={{ background: viewMode === 'grid' ? 'rgba(255,179,0,0.15)' : 'rgba(255,255,255,0.04)', color: viewMode === 'grid' ? '#FFB300' : 'rgba(255,255,255,0.4)' }}>
                <Grid size={14} />
              </button>
              <button onClick={() => setViewMode('list')} className="p-2 rounded-lg"
              style={{ background: viewMode === 'list' ? 'rgba(255,179,0,0.15)' : 'rgba(255,255,255,0.04)', color: viewMode === 'list' ? '#FFB300' : 'rgba(255,255,255,0.4)' }}>
                <List size={14} />
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between mb-4">
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
              <span style={{ color: '#FFB300', fontWeight: 700 }}>{filtered.length}</span> listings found
            </span>
            <Link href="/login"
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-700"
            style={{ background: '#FFB300', color: '#000' }}>
              Post Free Ad <ArrowRight size={12} />
            </Link>
          </div>

          {/* Grid view */}
          {viewMode === 'grid' ?
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {filtered.map((listing) =>
            <Link key={listing.id} href={`/listing/classifieds/${listing.id}`} className="rounded-2xl overflow-hidden block"
            style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${listing.is_featured ? 'rgba(255,179,0,0.25)' : 'rgba(255,255,255,0.07)'}` }}>
                  <div className="relative aspect-square">
                    <img src={listing.image_url} alt={listing.title}
                className="w-full h-full object-cover" loading="lazy" />
                    {listing.is_featured &&
                <span className="absolute top-2 left-2 text-[9px] px-1.5 py-0.5 rounded-md font-700"
                style={{ background: '#FFB300', color: '#000' }}>Featured</span>
                }
                    <button onClick={(e) => { e.preventDefault(); toggleSave(listing.id); }}
                className="absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center"
                style={{ background: savedIds.has(listing.id) ? '#FF4081' : 'rgba(0,0,0,0.5)' }}>
                      <Heart size={12} fill={savedIds.has(listing.id) ? '#fff' : 'none'} style={{ color: '#fff' }} />
                    </button>
                  </div>
                  <div className="p-2.5">
                    <p className="text-xs font-700 text-white line-clamp-2 leading-tight">{listing.title}</p>
                    <p className="text-sm font-800 mt-1" style={{ color: '#FFB300' }}>{formatPrice(listing.price)}</p>
                    <div className="flex items-center gap-1 mt-1">
                      <MapPin size={9} style={{ color: 'rgba(255,255,255,0.35)' }} />
                      <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.4)' }}>{listing.location}</span>
                    </div>
                  </div>
                </Link>
            )}
            </div> :

          <div className="flex flex-col gap-3">
              {filtered.map((listing) =>
            <div key={listing.id} className="flex gap-3 p-3 rounded-2xl"
            style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${listing.is_featured ? 'rgba(255,179,0,0.2)' : 'rgba(255,255,255,0.07)'}` }}>
                  <img src={listing.image_url} alt={listing.title}
              className="w-20 h-20 rounded-xl object-cover flex-shrink-0" loading="lazy" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-700 text-white line-clamp-1">{listing.title}</p>
                      <button onClick={() => toggleSave(listing.id)} className="flex-shrink-0">
                        <Heart size={14} fill={savedIds.has(listing.id) ? '#FF4081' : 'none'} style={{ color: savedIds.has(listing.id) ? '#FF4081' : 'rgba(255,255,255,0.3)' }} />
                      </button>
                    </div>
                    <p className="text-base font-800 mt-0.5" style={{ color: '#FFB300' }}>{formatPrice(listing.price)}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <div className="flex items-center gap-1">
                        <MapPin size={10} style={{ color: 'rgba(255,255,255,0.35)' }} />
                        <span className="text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>{listing.location}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Eye size={10} style={{ color: 'rgba(255,255,255,0.35)' }} />
                        <span className="text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>{listing.views}</span>
                      </div>
                      <span className="text-[10px] px-1.5 py-0.5 rounded-md"
                  style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.45)' }}>{listing.condition}</span>
                    </div>
                    <Link href={`/listing/classifieds/${listing.id}`}
                className="inline-flex items-center gap-1 mt-2 text-xs font-600"
                style={{ color: '#FFB300' }}>
                      View Details <ChevronRight size={11} />
                    </Link>
                  </div>
                </div>
            )}
            </div>
          }

          {/* CTA */}
          <div className="mt-8 p-6 rounded-3xl text-center"
          style={{ background: 'linear-gradient(135deg, rgba(255,179,0,0.1), rgba(255,109,0,0.1))', border: '1px solid rgba(255,179,0,0.2)' }}>
            <h3 className="text-lg font-800 text-white mb-2">See All {28450 .toLocaleString()} Listings</h3>
            <p className="text-sm mb-4" style={{ color: 'rgba(255,255,255,0.5)' }}>Sign up free to post ads, contact sellers, and get alerts for new listings.</p>
            <Link href="/login"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl font-700 text-sm"
            style={{ background: '#FFB300', color: '#000' }}>
              <Tag size={16} /> Post Free Ad
            </Link>
          </div>
        </div>
      </div>
    </AppLayout>);

}